<template>
  <div class="multi-select-chips">
    <AppSelect
      :items="items"
      :placeholder="placeholder"
      :propose-new="false"
      :close-on-select="false"
      :display-value="summaryText"
      @search="$emit('search', $event)"
      @nextPage="$emit('nextPage', $event)"
      @select="toggleItem"
    >
      <template #default="{ item }">
        <!--
          The row is the control and the box only reports it.

          Bootstrap's own classes, but deliberately not <b-form-checkbox>: that renders a
          <label for> bound to the input, and the visible square is that label's ::before.
          A click on it is forwarded to the input, so the row handler above took two clicks
          for one press and the selection turned on and straight back off - the very bug
          this row had when it was a <label> itself.

          Bootstrap's checked rules are class-based (.custom-control-input:checked ~
          .custom-control-label), so a plain span wears the look with nothing to forward.
          The input stays for the :checked state alone; it is inert, and Vue is the only
          thing that ever writes it.
        -->
        <div class="ms-item d-flex align-items-center" :class="{ 'is-selected': isSelected(item) }">
          <span class="ms-box custom-control custom-checkbox">
            <input
              type="checkbox"
              class="custom-control-input"
              :checked="isSelected(item)"
              tabindex="-1"
              aria-hidden="true"
            />
            <span class="custom-control-label" />
          </span>
          {{ item.label }}
          <!-- a note the caller hangs on the row, e.g. the city a floor keeps time by. It
               stays out of `label`, which is also what the closed field summarises -->
          <span v-if="item.hint" class="ms-hint ml-auto pl-3 text-muted">{{ item.hint }}</span>
        </div>
      </template>
    </AppSelect>

    <!-- outside the input, so the field's ellipsis can eat the names without ever
         eating the count or the way to clear them -->
    <div v-if="value.length" class="ms-tools">
      <span class="ms-count">{{ value.length }}</span>
      <button type="button" class="ms-clear" title="Clear selection" @click="clear">
        <i class="ion ion-md-close" />
      </button>
    </div>
  </div>
</template>

<script>
import AppSelect from '@/components/shared/AppSelect'

export default {
  name: 'MultiSelectChips',
  components: { AppSelect },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  computed: {
    summaryText() {
      return this.value.map((item) => item.label).join(', ')
    }
  },
  methods: {
    isSelected(item) {
      return this.value.some((selected) => selected.id === item.id)
    },
    toggleItem(item) {
      if (this.isSelected(item)) {
        this.$emit('input', this.value.filter((selected) => selected.id !== item.id))
      } else {
        this.$emit('input', [...this.value, item])
      }
    },
    clear() {
      this.$emit('input', [])
    }
  }
}
</script>

<style scoped lang="scss">
.multi-select-chips {
  min-width: 220px;
  position: relative;
}

.ms-tools {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  position: absolute;
  right: 0.6rem;
  // centred against the field rather than nudged down from its top, so the pair stays put
  // whatever the input's height works out to
  top: 50%;
  transform: translateY(-50%);
}

.ms-count {
  padding: 0 0.4rem;
  border-radius: 0.75rem;
  background: #e9ecef;
  color: #4e5155;
  font-size: 0.75rem;
  line-height: 1.25rem;
  pointer-events: none;
}

.ms-clear {
  align-items: center;
  background: none;
  border: 0;
  color: #a3a4a6;
  cursor: pointer;
  display: flex;
  padding: 0;

  &:hover,
  &:focus {
    color: #4e5155;
  }

  // the glyph carries its own line box, which rides above the centre line of the row it
  // shares with the count; flex centring only lands once it stops being inline text
  > i {
    display: block;
    font-size: 1rem;
    line-height: 1;
  }
}

// keep the summary clear of the count and the clear button
.multi-select-chips ::v-deep .app-select-search {
  padding-right: 3.5rem;
}

// type="search" draws a clear button of its own in WebKit, which sits beside ours and
// means something else: it empties the term the user typed, not the selection they made.
// One cross, one meaning.
.multi-select-chips ::v-deep .app-select-search::-webkit-search-cancel-button {
  display: none;
}

.ms-hint {
  font-size: 0.8125rem;
  white-space: nowrap;
}

.ms-item {
  cursor: pointer;

  &.is-selected {
    font-weight: 600;
  }
}

// never a click target: the row handles the click. Hung off the root like the rule above,
// because a bare .ms-box selector would rely on the scope id reaching markup that is
// rendered as another component's slot content
.multi-select-chips ::v-deep .ms-box {
  pointer-events: none;
}
</style>
